import React, { FC, useState } from 'react';

import { KVRows } from '../bits';
import { money } from '../data/format';
import type { ISarDraft } from '../data/types';

/**
 * THE PRE-FILLED SAR, on the transaction that tripped a control.
 *
 * Built server-side from records every time the drawer opens
 * (transaction.monitoring.api libs/fincen/sar-prefill), so it always reflects the
 * current alert and case. Nothing here files anything: a rule hit is evidence,
 * whether a SAR is owed is the officer's decision, and the draft says so.
 *
 * What it shows, in the order an officer needs it:
 *   1. whether a SAR is suggested at all, and why — or why not (shadow rules);
 *   2. what the system filled in, with the SOURCE of each value;
 *   3. what it could NOT fill, and why — the gaps are the officer's to-do list;
 *   4. the drafted narrative;
 *   5. once complete, the schema-valid XML for review.
 *
 * Subject details (DOB, address, ID) live on the onboarding record, which TM has
 * no link to yet, so they are entered here and saved ON THE CASE — which is why
 * the form appears only once the alert has been escalated.
 */

const fmtYmd = (s?: string | null) =>
	s && /^\d{8}$/.test(s) ? `${s.slice(0, 4)}-${s.slice(4, 6)}-${s.slice(6, 8)}` : '—';

const STATUS: Record<ISarDraft['status'], [string, string]> = {
	ready: ['pass', 'ready for review'],
	needs_information: ['review', 'needs information'],
	no_activity: ['test', 'no activity'],
	shadow_only: ['test', 'shadow rules only'],
};

const SOURCE_LABEL: Record<string, string> = {
	transaction: 'record',
	alert: 'alert',
	case: 'case',
	'rule-map': 'rule map',
	derived: 'derived',
	'filer-profile': 'filer profile',
	officer: 'officer',
	'system-clock': 'today',
};


/* A SUMMARY. Completing and generating the SAR happens in the pop-out opened
   from "Open SAR" at the very top of the drawer (./sar-modal); this section
   says what the controls found and what is still missing, under the evidence. */
export const SarDraftSection: FC<{
	draft: ISarDraft | null;
	loading: boolean;
}> = ({ draft, loading }) => {
	const [showNarrative, setShowNarrative] = useState(false);

	if (!draft) {
		return loading ? (
			<div className="dsec sar-draft" data-qa="sar-draft">
				<div className="sh">SAR draft</div>
				<div className="sar-note">Building the draft from records…</div>
			</div>
		) : null;
	}
	/* Nothing matched: no section at all. A "no SAR" panel on every clean
	   transaction is noise on the screen analysts spend all day in. */
	if (!draft.rules.length) return null;

	const [chip, chipText] = STATUS[draft.status];
	const a = draft.activity;
	const byLabel = (label: string) => draft.fields.find(f => f.label === label);
	const src = (label: string) => {
		const f = byLabel(label);
		return f ? <span className="sar-src">{SOURCE_LABEL[f.source] ?? f.source}</span> : null;
	};

	return (
		<div className="dsec sar-draft" data-qa="sar-draft" data-status={draft.status}>
			<div className="sh">
				SAR draft{' '}
				<span className={`nd ${chip}`} data-qa="sar-status">
					{chipText}
				</span>
				<span className="r2">
					{draft.caseNumber ? `case ${draft.caseNumber}` : 'not escalated'}
				</span>
			</div>

			<div className={`sar-note${draft.recommended ? ' rec' : ''}`} data-qa="sar-recommendation">
				{draft.recommendation}
			</div>

			{draft.recommended && (
				<>
					<table className="kv wide" data-qa="sar-fields">
						<tbody>
							<KVRows
								rows={[
									[
										'classification',
										<span key="c">
											{a.classifications.map(c => (
												<span key={c.subtypeId} className="sar-cls">
													{c.typeId}/{c.subtypeId}
													{c.otherText ? ` · ${c.otherText}` : ''}
												</span>
											))}
										</span>,
									],
									[
										'amount',
										<span key="a">
											{a.totalAmount !== undefined ? money(a.totalAmount, 'USD') : 'unknown'}{' '}
											{src('Total suspicious amount (USD)')}
										</span>,
									],
									[
										'activity',
										<span key="d">
											{fmtYmd(a.activityFromDate)}
											{a.activityToDate ? ` → ${fmtYmd(a.activityToDate)}` : ''} {src('Activity from')}
										</span>,
									],
									[
										'subject',
										<span key="s">
											{byLabel('Name')?.value ?? '—'} {src('Name')}
										</span>,
									],
									[
										'transactions',
										`${draft.transactions.length} · ${draft.rules.length} control${draft.rules.length === 1 ? '' : 's'}`,
									],
								]}
							/>
						</tbody>
					</table>

					<div className="sar-rules">
						{draft.rules.map(r => (
							<div key={r.ruleUuid} className={`sar-rule${r.mode === 'TEST' ? ' test' : ''}`}>
								<b>{r.ruleName}</b>
								<span className="mono">{r.ruleUuid}</span>
								{r.classificationLabel && <span className="sar-cls">{r.classificationLabel}</span>}
								{r.mode === 'TEST' && <span className="nd test">test — not classified</span>}
							</div>
						))}
					</div>

					{draft.gaps.length > 0 && (
						<div className="sar-gaps" data-qa="sar-gaps">
							<div className="sar-sub">
								{draft.gaps.length} field{draft.gaps.length === 1 ? '' : 's'} the system cannot fill
							</div>
							<ul>
								{draft.gaps.map(g => (
									<li key={g.field}>
										<b>{g.field}</b> — {g.why}
									</li>
								))}
							</ul>
						</div>
					)}
					{draft.validation.length > 0 && (
						<div className="sar-gaps bad">
							<div className="sar-sub">Schema problems</div>
							<ul>
								{draft.validation.map(v => (
									<li key={v}>{v}</li>
								))}
							</ul>
						</div>
					)}

					<button
						type="button"
						className="sar-toggle"
						onClick={() => setShowNarrative(v => !v)}
						data-qa="sar-narrative-toggle"
					>
						{showNarrative ? 'Hide' : 'Show'} drafted narrative ({a.narrative.length.toLocaleString()} chars)
					</button>
					{showNarrative && (
						<pre className="sar-narrative" data-qa="sar-narrative">
							{a.narrative}
						</pre>
					)}

					<div className="sar-note">
						Complete and generate the filing from <b>Open SAR</b> at the top of this drawer.
					</div>
				</>
			)}
		</div>
	);
};
